import type {
  FarmPortfolioEvent,
  FarmPortfolioEventKind,
  FarmPortfolioLineId,
} from "../../gameplay/farm-portfolio/types.ts";

export interface FarmPortfolioAudioController {
  readonly unlocked: boolean;
  consume(events: readonly FarmPortfolioEvent[]): void;
  unlock(): Promise<boolean>;
  setMuted(muted: boolean): void;
  dispose(): void;
}

export interface FarmPortfolioAudioOptions {
  readonly unlockTarget?: EventTarget;
  readonly contextFactory?: () => AudioContext | null;
  readonly maxVoices?: number;
}

interface Note {
  readonly delay: number;
  readonly duration: number;
  readonly frequency: number;
  readonly gain: number;
  readonly type: OscillatorType;
  readonly detune?: number;
}

const MASTER_GAIN = 0.26;
const LOOKAHEAD_SECONDS = 0.008;
const DEFAULT_MAX_VOICES = 20;

const GENERIC_CUES: Readonly<Partial<Record<FarmPortfolioEventKind, readonly Note[]>>> = Object.freeze({
  "investment-purchased": Object.freeze([
    note(0, 0.08, 392, 0.28, "triangle"),
    note(0.06, 0.1, 523.25, 0.26, "sine"),
    note(0.14, 0.14, 659.25, 0.24, "triangle"),
    note(0.26, 0.18, 784, 0.18, "sine"),
  ]),
  "hand-hired": Object.freeze([
    note(0, 0.06, 294, 0.24, "triangle"),
    note(0.05, 0.1, 440, 0.22, "sine"),
    note(0.12, 0.14, 554.37, 0.18, "triangle"),
    note(0.22, 0.16, 659.25, 0.14, "sine"),
  ]),
  "hand-assignment-applied": Object.freeze([
    note(0, 0.05, 330, 0.22, "triangle"),
    note(0.04, 0.075, 495, 0.2, "sine"),
    note(0.1, 0.1, 587.33, 0.14, "triangle"),
  ]),
  "sale-committed": Object.freeze([
    note(0, 0.04, 392, 0.2, "triangle"),
    note(0.04, 0.06, 523.25, 0.18, "sine"),
  ]),
  "cash-dropped": Object.freeze([
    note(0, 0.05, 330, 0.22, "triangle"),
    note(0.04, 0.08, 440, 0.18, "sine"),
    note(0.1, 0.1, 554.37, 0.14, "triangle"),
  ]),
  "cash-scooped": Object.freeze([
    note(0, 0.045, 523.25, 0.3, "triangle"),
    note(0.04, 0.065, 659.25, 0.28, "triangle"),
    note(0.085, 0.09, 783.99, 0.26, "sine"),
    note(0.16, 0.12, 1046.5, 0.2, "sine"),
    note(0.26, 0.14, 1318.5, 0.14, "triangle"),
  ]),
  "world-publication-committed": Object.freeze([
    note(0, 0.08, 349.23, 0.24, "triangle"),
    note(0.07, 0.12, 523.25, 0.24, "sine"),
    note(0.16, 0.16, 698.46, 0.22, "triangle"),
    note(0.28, 0.2, 880, 0.16, "sine"),
  ]),
  "command-rejected": Object.freeze([
    note(0, 0.07, 196, 0.12, "triangle"),
    note(0.055, 0.1, 146.83, 0.1, "sine"),
  ]),
  "action-cancelled": Object.freeze([
    note(0, 0.07, 220, 0.12, "triangle"),
    note(0.055, 0.095, 174.61, 0.1, "sine"),
  ]),
  "craft-delivery-committed": Object.freeze([
    note(0, 0.04, 392, 0.2, "triangle"),
    note(0.035, 0.065, 523.25, 0.18, "sine"),
    note(0.09, 0.09, 659.25, 0.12, "triangle"),
  ]),
  "craft-order-fulfilled": Object.freeze([
    note(0, 0.05, 523.25, 0.26, "triangle"),
    note(0.045, 0.07, 659.25, 0.24, "triangle"),
    note(0.1, 0.12, 880, 0.22, "sine"),
    note(0.2, 0.16, 1174.7, 0.16, "sine"),
    note(0.32, 0.18, 1318.5, 0.12, "triangle"),
  ]),
  "craft-order-cancelled": Object.freeze([
    note(0, 0.07, 246.94, 0.12, "triangle"),
    note(0.055, 0.1, 185, 0.1, "sine"),
  ]),
});

const LINE_CUES: Readonly<Record<FarmPortfolioLineId, Readonly<{ source: readonly Note[]; transfer: readonly Note[]; refine: readonly Note[] }>>> = Object.freeze({
  poultry: Object.freeze({
    source: Object.freeze([note(0, 0.055, 196, 0.25, "triangle"), note(0.04, 0.08, 293.66, 0.18, "sine")]),
    transfer: Object.freeze([note(0, 0.06, 130.81, 0.25, "triangle"), note(0.045, 0.08, 196, 0.16, "sine")]),
    refine: Object.freeze([note(0, 0.045, 520, 0.16, "square"), note(0.07, 0.055, 430, 0.15, "square")]),
  }),
  garden: Object.freeze({
    source: Object.freeze([note(0, 0.05, 233.08, 0.23, "triangle", -8), note(0.035, 0.085, 349.23, 0.17, "sine", 6)]),
    transfer: Object.freeze([note(0, 0.055, 164.81, 0.22, "triangle"), note(0.04, 0.08, 246.94, 0.16, "sine")]),
    refine: Object.freeze([note(0, 0.045, 293.66, 0.18, "square"), note(0.055, 0.065, 369.99, 0.16, "triangle"), note(0.105, 0.09, 440, 0.14, "sine")]),
  }),
  orchard: Object.freeze({
    source: Object.freeze([note(0, 0.06, 261.63, 0.22, "sine"), note(0.045, 0.09, 392, 0.17, "triangle")]),
    transfer: Object.freeze([note(0, 0.06, 146.83, 0.23, "triangle"), note(0.045, 0.085, 220, 0.16, "sine")]),
    refine: Object.freeze([note(0, 0.05, 110, 0.2, "sine"), note(0.05, 0.085, 220, 0.17, "triangle"), note(0.11, 0.1, 329.63, 0.14, "sine")]),
  }),
  grain: Object.freeze({
    source: Object.freeze([note(0, 0.045, 185, 0.22, "triangle"), note(0.04, 0.09, 277.18, 0.16, "sine")]),
    transfer: Object.freeze([note(0, 0.055, 138.59, 0.2, "triangle"), note(0.045, 0.08, 207.65, 0.15, "sine")]),
    refine: Object.freeze([note(0, 0.04, 98, 0.2, "square"), note(0.055, 0.07, 196, 0.16, "triangle"), note(0.12, 0.1, 293.66, 0.13, "sine")]),
  }),
  hay: Object.freeze({
    source: Object.freeze([note(0, 0.05, 174.61, 0.2, "triangle", -7), note(0.04, 0.085, 261.63, 0.15, "sine", 5)]),
    transfer: Object.freeze([note(0, 0.06, 116.54, 0.22, "triangle"), note(0.05, 0.085, 174.61, 0.15, "sine")]),
    refine: Object.freeze([note(0, 0.045, 146.83, 0.18, "square"), note(0.06, 0.08, 220, 0.15, "triangle")]),
  }),
  dairy: Object.freeze({
    source: Object.freeze([note(0, 0.065, 220, 0.2, "sine"), note(0.05, 0.095, 329.63, 0.16, "triangle")]),
    transfer: Object.freeze([note(0, 0.055, 130.81, 0.21, "triangle"), note(0.05, 0.09, 196, 0.15, "sine")]),
    refine: Object.freeze([note(0, 0.05, 164.81, 0.18, "sine"), note(0.055, 0.08, 246.94, 0.16, "triangle"), note(0.12, 0.11, 369.99, 0.13, "sine")]),
  }),
  apiary: Object.freeze({
    source: Object.freeze([note(0, 0.045, 311.13, 0.2, "triangle", 7), note(0.04, 0.085, 466.16, 0.15, "sine", -4)]),
    transfer: Object.freeze([note(0, 0.05, 196, 0.2, "triangle"), note(0.045, 0.08, 293.66, 0.15, "sine")]),
    refine: Object.freeze([note(0, 0.04, 246.94, 0.17, "square"), note(0.05, 0.075, 369.99, 0.16, "triangle"), note(0.11, 0.1, 554.37, 0.13, "sine")]),
  }),
  sheep: Object.freeze({
    source: Object.freeze([note(0, 0.055, 207.65, 0.2, "sine"), note(0.045, 0.09, 311.13, 0.15, "triangle")]),
    transfer: Object.freeze([note(0, 0.055, 155.56, 0.2, "triangle"), note(0.045, 0.085, 233.08, 0.15, "sine")]),
    refine: Object.freeze([note(0, 0.045, 174.61, 0.17, "square"), note(0.06, 0.08, 261.63, 0.15, "triangle"), note(0.12, 0.1, 392, 0.13, "sine")]),
  }),
});

export function createFarmPortfolioAudio(options: FarmPortfolioAudioOptions = {}): FarmPortfolioAudioController {
  const target = options.unlockTarget ?? (typeof window === "undefined" ? undefined : window);
  const contextFactory = options.contextFactory ?? defaultContextFactory;
  const maxVoices = Math.max(1, Math.trunc(options.maxVoices ?? DEFAULT_MAX_VOICES));
  const activeVoices = new Set<OscillatorNode>();
  const removals: Array<() => void> = [];

  let context: AudioContext | null = null;
  let master: GainNode | null = null;
  let disposed = false;
  let muted = false;
  let isUnlocked = false;
  let lastSequence = 0;

  const ensureContext = (): AudioContext | null => {
    if (disposed) return null;
    if (context !== null) return context;
    try {
      context = contextFactory();
      if (context === null) return null;
      master = context.createGain();
      master.gain.value = muted ? 0 : MASTER_GAIN;
      master.connect(context.destination);
      return context;
    } catch {
      context = null;
      master = null;
      return null;
    }
  };

  const unlock = async (): Promise<boolean> => {
    if (disposed) return false;
    const audio = ensureContext();
    if (audio === null) return false;
    try {
      if (audio.state === "suspended") await audio.resume();
      isUnlocked = audio.state === "running";
      return isUnlocked;
    } catch {
      return false;
    }
  };

  const onGesture = (): void => { void unlock(); };
  if (target !== undefined) {
    for (const type of ["pointerdown", "touchstart", "keydown"] as const) {
      target.addEventListener(type, onGesture, { passive: true });
      removals.push(() => target.removeEventListener(type, onGesture));
    }
  }

  const cueFor = (event: FarmPortfolioEvent): readonly Note[] | undefined => {
    const generic = GENERIC_CUES[event.kind];
    if (generic !== undefined) return generic;
    if (event.lineId === null) return undefined;
    const family = LINE_CUES[event.lineId];
    if (event.kind === "source-committed" || event.kind === "source-ready") return family.source;
    if (event.kind === "transfer-committed") return family.transfer;
    if (event.kind === "dairy-care-committed") return family.transfer;
    if (event.kind === "refinement-committed") return family.refine;
    return undefined;
  };

  const play = (cue: readonly Note[]): void => {
    if (!isUnlocked || muted || disposed || context === null || master === null) return;
    const start = context.currentTime + LOOKAHEAD_SECONDS;
    for (const descriptor of cue) {
      if (activeVoices.size >= maxVoices) break;
      const oscillator = context.createOscillator();
      const envelope = context.createGain();
      oscillator.type = descriptor.type;
      oscillator.frequency.setValueAtTime(descriptor.frequency, start + descriptor.delay);
      oscillator.detune.setValueAtTime(descriptor.detune ?? 0, start + descriptor.delay);
      envelope.gain.setValueAtTime(0.0001, start + descriptor.delay);
      envelope.gain.exponentialRampToValueAtTime(
        Math.max(0.0001, descriptor.gain),
        start + descriptor.delay + Math.min(0.018, descriptor.duration * 0.35),
      );
      envelope.gain.exponentialRampToValueAtTime(0.0001, start + descriptor.delay + descriptor.duration);
      oscillator.connect(envelope);
      envelope.connect(master);
      activeVoices.add(oscillator);
      oscillator.addEventListener("ended", () => {
        activeVoices.delete(oscillator);
        oscillator.disconnect();
        envelope.disconnect();
      }, { once: true });
      oscillator.start(start + descriptor.delay);
      oscillator.stop(start + descriptor.delay + descriptor.duration + 0.01);
    }
  };

  return {
    get unlocked(): boolean { return isUnlocked; },
    consume(events: readonly FarmPortfolioEvent[]): void {
      if (disposed) return;
      for (const event of events) {
        if (!Number.isSafeInteger(event.sequence) || event.sequence <= lastSequence) continue;
        lastSequence = event.sequence;
        const cue = cueFor(event);
        if (cue !== undefined) play(cue);
      }
    },
    unlock,
    setMuted(nextMuted: boolean): void {
      if (disposed) return;
      muted = nextMuted;
      if (master !== null && context !== null) {
        master.gain.setValueAtTime(muted ? 0 : MASTER_GAIN, context.currentTime);
      }
    },
    dispose(): void {
      if (disposed) return;
      disposed = true;
      for (const remove of removals.splice(0)) remove();
      for (const voice of [...activeVoices]) {
        try { voice.stop(); } catch { /* The voice may already have ended. */ }
      }
      activeVoices.clear();
      master?.disconnect();
      master = null;
      const ownedContext = context;
      context = null;
      if (ownedContext !== null && ownedContext.state !== "closed") {
        void ownedContext.close().catch(() => undefined);
      }
      isUnlocked = false;
    },
  };
}

function note(
  delay: number,
  duration: number,
  frequency: number,
  gain: number,
  type: OscillatorType,
  detune?: number,
): Note {
  return Object.freeze({ delay, duration, frequency, gain, type, ...(detune === undefined ? {} : { detune }) });
}

function defaultContextFactory(): AudioContext | null {
  if (typeof AudioContext === "undefined") return null;
  return new AudioContext({ latencyHint: "interactive" });
}
